import { firestoreAction } from "@/store/firestoreBinding";
import { db } from "@/firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";

const sectionsRef = db.collection("sections");
const sectionsLevelHistoryRef = db.collection("sectionsLevelHistory");
const sectionsVideosRef = db.collection("sectionsVideos");
const sectionsToposRef = db.collection("sectionsTopos");
const sectionsDirectionsRef = db.collection("sectionsDirections");

function normalizeSectionClassLevels(section = {}) {
  const normalizedSection = { ...section };
  const hasClassLevel = Object.prototype.hasOwnProperty.call(normalizedSection, "class_level");
  const hasClassLevelMin = Object.prototype.hasOwnProperty.call(normalizedSection, "class_level_min");

  // Backward compatibility: legacy class_level represented min level.
  if (!hasClassLevelMin && hasClassLevel) {
    normalizedSection.class_level_min = normalizedSection.class_level;
  }

  // New schema: class_level is median/default level.
  if (!hasClassLevel && hasClassLevelMin) {
    normalizedSection.class_level = normalizedSection.class_level_min;
  }

  // Ensure median defaults to min while migration is in progress.
  if (
    hasClassLevelMin &&
    (normalizedSection.class_level === undefined ||
      normalizedSection.class_level === null ||
      normalizedSection.class_level === "")
  ) {
    normalizedSection.class_level = normalizedSection.class_level_min;
  }

  return normalizedSection;
}

function normalizeSectionPortages(section = {}) {
  const normalizedSection = { ...section };
  const hasPortage = Object.prototype.hasOwnProperty.call(normalizedSection, "portage");
  const hasPortageMin = Object.prototype.hasOwnProperty.call(normalizedSection, "portage_min");
  const hasPortageMax = Object.prototype.hasOwnProperty.call(normalizedSection, "portage_max");

  if (!hasPortage && hasPortageMin) {
    normalizedSection.portage = normalizedSection.portage_min;
  } else if (!hasPortage && hasPortageMax) {
    normalizedSection.portage = normalizedSection.portage_max;
  }

  delete normalizedSection.portage_min;
  delete normalizedSection.portage_max;

  return normalizedSection;
}

export default {
  namespaced: true,
  state: {
    sections: [], // Initialize sections as an empty array
  },
  getters: {
    sections: (state) => {
      return state.sections;
    },
  },
  actions: {
    bindSections: firestoreAction(({ bindFirestoreRef }) => {
      return bindFirestoreRef("sections", sectionsRef.orderBy("alias").where("published", "==", true));
    }),
    bindSectionsAdmin: firestoreAction((context) => {
      context.bindFirestoreRef("sections", sectionsRef.orderBy("alias"));
    }),
    async getSections() {
      try {
        const snapshot = await sectionsRef.orderBy("alias").get();

        if (snapshot.empty) {
          return [];
        }

        return snapshot.docs.map((doc) => {
          return {
            ...normalizeSectionPortages(normalizeSectionClassLevels(doc.data())),
            id: doc.id,
          };
        });
      } catch (error) {
        console.error("Error fetching sections:", error);
        return [];
      }
    },
    querySectionsByLikedIds: (_context, likedIds) => {
      // likedIds will now be a batch of 10 or fewer IDs
      if (!likedIds || likedIds.length === 0) {
        return Promise.resolve({ docs: [] });
      }

      try {
        // This query will now always have 10 or fewer items in the 'in' clause
        return db.collection("sections").where(firebase.firestore.FieldPath.documentId(), "in", likedIds).get();
      } catch (error) {
        console.error("Error in querySectionsByLikedIds:", error);
        return Promise.reject(error);
      }
    },
    async findSectionsByAlias(sectionAlias) {
      if (typeof sectionAlias !== "string" || sectionAlias.trim() === "") {
        throw new Error("Invalid sectionAlias provided to findSectionsByAlias");
      }

      const snapshot = await sectionsRef.where("alias", "==", sectionAlias).get();
      if (snapshot.empty) {
        return [];
      }

      const sections = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      return sections;
    },
    async getExtrapolationBySectionId(id) {
      const extrapolationDoc = await sectionsRef.doc(id).collection("extrapolation").get();

      if (extrapolationDoc.empty) {
        return;
      }

      var extrapolationData = extrapolationDoc.docs.map((doc) => {
        return { ...doc.data(), id: doc.id };
      });

      return extrapolationData;
    },
    async setExtrapolation(sectionId, extrapolation) {
      sectionsRef
        .doc(sectionId)
        .collection("extrapolation")
        .doc(extrapolation.id)
        .set(
          {
            gauge_id: extrapolation.gauge_id,
            gauge_station_name: extrapolation.gauge_station_name,
            type: extrapolation.type,
            weighting_up: extrapolation.weighting_up,
            weighting_down: extrapolation.weighting_down,
          },
          { merge: true },
        )
        .catch((error) => {
          console.log(error);
        });
    },
    async findSectionFlowHistory(id) {
      if (!id || typeof id !== "string") {
        throw new Error("Invalid section ID provided to findSectionFlowHistory");
      }

      const sectionsLevelHistoryDoc = sectionsLevelHistoryRef.doc(id);
      const doc = await sectionsLevelHistoryDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc;
    },
    async deleteSectionFlowHistory(sectionId) {
      const sectionLevelHistoryRefDelete = sectionsLevelHistoryRef.doc(sectionId);
      await sectionLevelHistoryRefDelete.delete();
    },
    async findSectionVideos(id) {
      const sectionsVideosDoc = sectionsVideosRef.doc(id);
      const doc = await sectionsVideosDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc.data();
    },
    async setSectionVideos(sectionId, videos) {
      const sectionsVideosDoc = sectionsVideosRef.doc(sectionId);

      if (videos) {
        sectionsVideosDoc.set({
          links: JSON.stringify(videos),
        });
      }
    },
    async deleteSectionVideos(sectionId) {
      const sectionVideosRefDelete = sectionsVideosRef.doc(sectionId);
      await sectionVideosRefDelete.delete();
    },
    async findSectionsTopos(id) {
      const sectionsToposDoc = sectionsToposRef.doc(id);
      const doc = await sectionsToposDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc.data();
    },
    async setSectionsTopos(sectionId, topos) {
      const sectionsToposDoc = sectionsToposRef.doc(sectionId);

      if (topos) {
        sectionsToposDoc.set({
          links: JSON.stringify(topos),
        });
      }
    },
    async deleteSectionsTopos(sectionId) {
      const sectionToposRefDelete = sectionsToposRef.doc(sectionId);
      await sectionToposRefDelete.delete();
    },
    async findSectionsDirections(id) {
      const sectionsDirectionsDoc = sectionsDirectionsRef.doc(id);
      const doc = await sectionsDirectionsDoc.get();

      if (!doc.exists) {
        return {
          markers: "[]",
          sectionPath: "[]",
        };
      }

      return doc.data();
    },
    async setSectionsDirections(sectionId, directions) {
      const sectionsDirectionsDoc = sectionsDirectionsRef.doc(sectionId);
      sectionsDirectionsDoc.set(
        {
          markers: directions.markers,
          sectionPath: directions.sectionPath,
        },
        { merge: true },
      );
    },
    async deleteSectionsDirections(sectionId) {
      const sectionDirectionsRefDelete = sectionsDirectionsRef.doc(sectionId);
      await sectionDirectionsRefDelete.delete();
    },
    async addSection(section) {
      const normalizedSection = normalizeSectionPortages(normalizeSectionClassLevels(section));
      normalizedSection.alias = this.createAlias(normalizedSection.name);
      const result = await sectionsRef.add(normalizedSection);
      console.log(result.id);
      return result.id;
    },
    async deleteSection(section) {
      await this.deleteSectionsDirections(section.id);
      await this.deleteSectionFlowHistory(section.id);
      await this.deleteSectionsTopos(section.id);
      await this.deleteSectionVideos(section.id);

      const sectionRefDelete = sectionsRef.doc(section.id);
      await sectionRefDelete.delete();
    },
    async updateSection(section) {
      const normalizedSection = normalizeSectionPortages(normalizeSectionClassLevels(section));
      normalizedSection.alias = this.createAlias(normalizedSection.river + " " + normalizedSection.name);
      const sectionRefUpdate = sectionsRef.doc(normalizedSection.id);
      await sectionRefUpdate.update(normalizedSection);
    },
    createAlias(stringToAlias) {
      stringToAlias = stringToAlias.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      return stringToAlias
        .replace(/[^a-z0-9\s]/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
    },
    async findSectionsByGaugeId(gaugeId) {
      if (!gaugeId || typeof gaugeId !== "string") {
        return [];
      }
      const sectionsSnapshot = await sectionsRef.orderBy("alias").get();
      if (sectionsSnapshot.empty) {
        return [];
      }
      const sections = sectionsSnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      const results = await Promise.all(
        sections.map(async (section) => {
          const extrapolationSnapshot = await sectionsRef
            .doc(section.id)
            .collection("extrapolation")
            .where("gauge_id", "==", gaugeId)
            .get();
          return extrapolationSnapshot.empty ? null : section;
        }),
      );
      return results.filter(Boolean);
    },
  },
};
